"use client";

import { ChevronDown, Plus, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import type { useMinimumHourlyRate } from "@/hooks/use-minimum-hourly-rate";

import { Field, Input, RateInput, Textarea } from "./profile-fields";
import type {
  EducationItem,
  ExperienceItem,
  ProfileDraft,
} from "./profile-types";

const SUMMARY_MAX = 1000;

interface SubStepProps {
  draft: ProfileDraft;
  onChange: (patch: Partial<ProfileDraft>) => void;
}

/* ─────────────────────────────────────────────────────────────────── */
/* 1. About you                                                          */
/* ─────────────────────────────────────────────────────────────────── */

export function AboutYouStep({ draft, onChange }: SubStepProps) {
  const t = useTranslations("Join.about");
  const summary = draft.summary ?? "";
  return (
    <div className="flex w-full max-w-[560px] flex-col gap-[22px]">
      <header>
        <h1 className="text-foreground m-0 text-[26px] leading-[1.2] font-medium tracking-[-0.02em]">
          {t("heading")}
        </h1>
        <p className="mt-2 text-[14px] leading-[1.5] text-[#606060] dark:text-neutral-400">
          {t("description")}
        </p>
      </header>

      <Field label={t("fullName")}>
        <Input
          value={draft.fullName ?? ""}
          onChange={(v) => {
            onChange({ fullName: v || null });
          }}
        />
      </Field>

      <Field label={t("headline")}>
        <Input
          placeholder={t("headlinePlaceholder")}
          value={draft.headline ?? ""}
          onChange={(v) => {
            onChange({ headline: v || null });
          }}
        />
      </Field>

      <Field label={t("summary")} count={`${summary.length} / ${SUMMARY_MAX}`}>
        <Textarea
          rows={8}
          minHeightClass="min-h-[200px]"
          placeholder={t("summaryPlaceholder")}
          value={summary}
          onChange={(v) => {
            onChange({ summary: v || null });
          }}
        />
      </Field>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* 2. Experience                                                         */
/* ─────────────────────────────────────────────────────────────────── */

export function ExperienceStep({ draft, onChange }: SubStepProps) {
  const t = useTranslations("Join.experience");
  const tFields = useTranslations("Join.fields");
  const update = (id: string, patch: Partial<ExperienceItem>) => {
    onChange({
      experiences: draft.experiences.map((e) =>
        e.id === id ? { ...e, ...patch } : e,
      ),
    });
  };
  const remove = (id: string) => {
    onChange({ experiences: draft.experiences.filter((e) => e.id !== id) });
  };
  const add = (): string => {
    const id = crypto.randomUUID();
    onChange({
      experiences: [
        ...draft.experiences,
        {
          id,
          company: "",
          role: "",
          startedAt: "",
          endedAt: "",
        },
      ],
    });
    return id;
  };

  const subtitle =
    draft.experiences.length === 0
      ? "Add the roles that show your range."
      : `${draft.experiences.length} role${draft.experiences.length === 1 ? "" : "s"} imported. Tap any to refine.`;

  return (
    <div className="flex w-full max-w-[560px] flex-col gap-[22px]">
      <header>
        <h1 className="text-foreground m-0 text-[26px] leading-[1.2] font-medium tracking-[-0.02em]">
          {t("heading")}
        </h1>
        <p className="mt-2 text-[14px] leading-[1.5] text-[#606060] dark:text-neutral-400">
          {subtitle}
        </p>
      </header>

      <AccordionList
        items={draft.experiences}
        onAdd={add}
        onRemove={remove}
        addLabel={t("addLabel")}
        emptyState={t("emptyState")}
        getInitial={(exp) => firstChar(exp.company || exp.role)}
        getTitle={(exp) =>
          formatExperienceTitle(exp.role, exp.company) || t("newItem")
        }
        getMeta={(exp) => formatRange(exp.startedAt, exp.endedAt, tFields)}
        renderBody={(exp) => (
          <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-2">
            {/* Role takes the full row so Company can sit beside its
                website: the two describe one employer, and pairing them
                keeps the rest in their natural pairs with no orphan. */}
            <div className="sm:col-span-2">
              <Field label={t("role")}>
                <Input
                  value={exp.role ?? ""}
                  onChange={(v) => {
                    update(exp.id, { role: v || null });
                  }}
                />
              </Field>
            </div>
            <Field label={t("company")}>
              <Input
                value={exp.company}
                onChange={(v) => {
                  update(exp.id, { company: v });
                }}
              />
            </Field>
            <Field label={t("companyWebsite")}>
              <Input
                value={exp.companyDomain ?? ""}
                onChange={(v) => {
                  update(exp.id, { companyDomain: v || null });
                }}
              />
            </Field>
            <Field label={t("start")}>
              <Input
                mono
                placeholder={t("startPlaceholder")}
                value={exp.startedAt ?? ""}
                onChange={(v) => {
                  update(exp.id, { startedAt: v || null });
                }}
              />
            </Field>
            <Field label={t("end")}>
              <Input
                mono
                placeholder={t("endPlaceholder")}
                value={exp.endedAt ?? ""}
                onChange={(v) => {
                  update(exp.id, { endedAt: v || null });
                }}
              />
            </Field>
            <Field label={t("city")}>
              <Input
                placeholder={t("cityPlaceholder")}
                value={exp.city ?? ""}
                onChange={(v) => {
                  update(exp.id, { city: v || null });
                }}
              />
            </Field>
            <Field label={t("country")}>
              <Input
                placeholder={t("countryPlaceholder")}
                value={exp.country ?? ""}
                onChange={(v) => {
                  update(exp.id, { country: v || null });
                }}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t("description")} optional>
                <Textarea
                  rows={3}
                  placeholder={t("descriptionPlaceholder")}
                  value={exp.description ?? ""}
                  onChange={(v) => {
                    update(exp.id, { description: v || null });
                  }}
                />
              </Field>
            </div>
          </div>
        )}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* 3. Education                                                          */
/* ─────────────────────────────────────────────────────────────────── */

export function EducationStep({ draft, onChange }: SubStepProps) {
  const t = useTranslations("Join.education");
  const tFields = useTranslations("Join.fields");
  const update = (id: string, patch: Partial<EducationItem>) => {
    onChange({
      educations: draft.educations.map((e) =>
        e.id === id ? { ...e, ...patch } : e,
      ),
    });
  };
  const remove = (id: string) => {
    onChange({ educations: draft.educations.filter((e) => e.id !== id) });
  };
  const add = (): string => {
    const id = crypto.randomUUID();
    onChange({
      educations: [
        ...draft.educations,
        {
          id,
          school: "",
          degree: "",
          major: "",
          startedAt: "",
          endedAt: "",
        },
      ],
    });
    return id;
  };

  return (
    <div className="flex w-full max-w-[560px] flex-col gap-[22px]">
      <header>
        <h1 className="text-foreground m-0 text-[26px] leading-[1.2] font-medium tracking-[-0.02em]">
          {t("heading")}
        </h1>
        <p className="mt-2 text-[14px] leading-[1.5] text-[#606060] dark:text-neutral-400">
          {t("description")}
        </p>
      </header>

      <AccordionList
        items={draft.educations}
        onAdd={add}
        onRemove={remove}
        addLabel={t("addLabel")}
        emptyState={t("emptyState")}
        getInitial={(ed) => firstChar(ed.school || ed.degree)}
        getTitle={(ed) =>
          formatEducationTitle(ed.degree, ed.school) || t("newItem")
        }
        getMeta={(ed) => formatRange(ed.startedAt, ed.endedAt, tFields)}
        renderBody={(ed) => (
          <div className="grid grid-cols-1 gap-[14px] sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label={t("school")}>
                <Input
                  value={ed.school}
                  onChange={(v) => {
                    update(ed.id, { school: v });
                  }}
                />
              </Field>
            </div>
            <Field label={t("degree")}>
              <Input
                placeholder={t("degreePlaceholder")}
                value={ed.degree ?? ""}
                onChange={(v) => {
                  update(ed.id, { degree: v || null });
                }}
              />
            </Field>
            <Field label={t("major")} optional>
              <Input
                placeholder={t("majorPlaceholder")}
                value={ed.major ?? ""}
                onChange={(v) => {
                  update(ed.id, { major: v || null });
                }}
              />
            </Field>
            <Field label={t("start")}>
              <Input
                mono
                placeholder={t("startPlaceholder")}
                value={ed.startedAt ?? ""}
                onChange={(v) => {
                  update(ed.id, { startedAt: v || null });
                }}
              />
            </Field>
            <Field label={t("end")}>
              <Input
                mono
                placeholder={t("endPlaceholder")}
                value={ed.endedAt ?? ""}
                onChange={(v) => {
                  update(ed.id, { endedAt: v || null });
                }}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t("gpa")} optional>
                <Input
                  mono
                  placeholder="3.8"
                  value={ed.gpa ?? ""}
                  onChange={(v) => {
                    update(ed.id, { gpa: v || null });
                  }}
                />
              </Field>
            </div>
          </div>
        )}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* 4. Rate                                                               */
/* ─────────────────────────────────────────────────────────────────── */

export function RateStep({
  rate,
}: {
  rate: ReturnType<typeof useMinimumHourlyRate>;
}) {
  const t = useTranslations("Join.rate");
  return (
    <div className="flex w-full max-w-[560px] flex-col gap-[22px]">
      <header>
        <h1 className="text-foreground m-0 text-[26px] leading-[1.2] font-medium tracking-[-0.02em]">
          {t("heading")}
        </h1>
        <p className="mt-2 text-[14px] leading-[1.5] text-[#606060] dark:text-neutral-400">
          {t("description")}
        </p>
      </header>

      <div className="flex flex-col gap-2.5">
        <div className="max-w-[320px]">
          <RateInput
            value={rate.value}
            symbol={rate.currencySymbol}
            onChange={rate.setValue}
            onBlur={rate.blur}
          />
        </div>

        {rate.showingUsdFallback ? (
          <p className="text-[12.5px] leading-[1.4] text-red-600 dark:text-red-400">
            {t("conversionUnavailable")}
          </p>
        ) : (
          <p className="text-[12.5px] leading-[1.4] text-neutral-500 dark:text-neutral-400">
            {t("paidIn", { currency: rate.currencyCode })}
          </p>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* AccordionList; V4 design (hairline rules, 32px tile + initial)      */
/* ─────────────────────────────────────────────────────────────────── */

interface HasId {
  id: string;
}

interface AccordionListProps<T extends HasId> {
  items: T[];
  onAdd: () => string;
  onRemove: (id: string) => void;
  addLabel: string;
  emptyState: string;
  getInitial: (item: T) => string;
  getTitle: (item: T) => string;
  getMeta: (item: T) => string;
  renderBody: (item: T) => React.ReactNode;
}

function AccordionList<T extends HasId>({
  items,
  onAdd,
  onRemove,
  addLabel,
  emptyState,
  getInitial,
  getTitle,
  getMeta,
  renderBody,
}: AccordionListProps<T>) {
  const t = useTranslations("Join.fields");
  const [openId, setOpenId] = useState<string | null>(
    () => items[0]?.id ?? null,
  );

  useEffect(() => {
    if (openId && !items.some((it) => it.id === openId)) {
      setOpenId(items[0]?.id ?? null);
    }
  }, [items, openId]);

  const handleAdd = () => {
    const newId = onAdd();
    setOpenId(newId);
  };

  return (
    <div>
      {items.length === 0 ? (
        <p className="border-border border-t py-5 text-[13px] text-neutral-500 italic dark:text-neutral-400">
          {emptyState}
        </p>
      ) : (
        items.map((item) => {
          const isOpen = item.id === openId;
          const title = getTitle(item);
          const meta = getMeta(item);
          return (
            <div key={item.id} className="border-border border-t py-5">
              <button
                type="button"
                onClick={() => {
                  setOpenId(isOpen ? null : item.id);
                }}
                className="flex w-full cursor-pointer items-center justify-between gap-4 text-left"
              >
                <div className="flex min-w-0 items-center gap-[14px]">
                  <span className="bg-marketing-blue/10 text-marketing-blue dark:text-marketing-blue-light flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-mono text-[12px] font-medium">
                    {getInitial(item) || "?"}
                  </span>
                  <span className="text-foreground truncate text-[16px] font-medium tracking-[-0.01em]">
                    {title}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-[14px]">
                  <span className="font-mono text-[12px] tracking-[0.02em] text-neutral-500 dark:text-neutral-400">
                    {meta}
                  </span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-neutral-500 transition-transform duration-150 dark:text-neutral-400 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>
              {isOpen && (
                <div className="mt-[18px]">
                  {renderBody(item)}
                  <button
                    type="button"
                    onClick={() => {
                      onRemove(item.id);
                    }}
                    className="hover:text-foreground mt-3 inline-flex cursor-pointer items-center gap-1.5 text-[13px] text-neutral-500 transition-colors dark:text-neutral-400"
                  >
                    <X className="h-[13px] w-[13px]" />
                    {t("remove")}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}

      <div className="border-border border-t pt-4">
        <button
          type="button"
          onClick={handleAdd}
          className="text-marketing-blue dark:text-marketing-blue-light hover:text-marketing-blue-dark inline-flex cursor-pointer items-center gap-1.5 text-[14px] font-medium transition-colors"
        >
          <Plus className="h-[14px] w-[14px]" strokeWidth={2.2} />
          {addLabel}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Helpers                                                                */
/* ─────────────────────────────────────────────────────────────────── */

function firstChar(value: string | null | undefined): string {
  return value?.trim().charAt(0).toUpperCase() ?? "";
}

function formatExperienceTitle(role: string | null, company: string): string {
  const parts = [role, company].filter(Boolean);
  return parts.join(" · ");
}

function formatEducationTitle(degree: string | null, school: string): string {
  const parts = [degree, school].filter(Boolean);
  return parts.join(" · ");
}

/**
 * `t` is threaded in rather than read from a hook: this runs inside
 * `getMeta`, a render callback the AccordionList calls per item, and a module
 * function is not a component.
 */
function formatRange(
  start: string | null,
  end: string | null,
  t: ReturnType<typeof useTranslations<"Join.fields">>,
): string {
  const s = formatYear(start);
  const e = formatYear(end);
  if (!s && !e) {
    return "";
  }
  if (s && !e) {
    return t("toPresent", { start: s });
  }
  if (!s && e) {
    return e;
  }
  return t("range", { start: s, end: e });
}

function formatYear(value: string | null): string {
  if (!value) {
    return "";
  }
  const match = value.match(/^(\d{4})/);
  return match?.[1] ?? value;
}
